import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Pencil, Trash2, Home, Briefcase, CreditCard } from 'lucide-react';
import { assetRegisterAPI } from '../lib/api';
import { formatCurrency } from '../lib/chartFormatters';
import { toast } from 'sonner';

const CATEGORY_META = {
  real_estate: { label: 'Real Estate', icon: Home, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  other_asset: { label: 'Other Asset', icon: Briefcase, color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  liability: { label: 'Liability', icon: CreditCard, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const CURRENCIES = ['GBP', 'EUR', 'USD', 'DKK'];

function EntryForm({ initial, onSave, onCancel, entityId, saving, defaultCurrency }) {
  const [label, setLabel] = useState(initial?.label || '');
  const [category, setCategory] = useState(initial?.category || 'real_estate');
  const [amount, setAmount] = useState(initial?.amount?.toString() || '');
  const [currency, setCurrency] = useState(initial?.currency || defaultCurrency || 'GBP');
  const [asOfDate, setAsOfDate] = useState(initial?.as_of_date || new Date().toISOString().slice(0, 10));

  const handleSubmit = (e) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (!label.trim()) { toast.error('Label is required'); return; }
    if (isNaN(parsed) || parsed < 0) { toast.error('Amount must be a non-negative number'); return; }
    onSave({
      entity_id: entityId,
      label: label.trim(),
      category,
      amount: parsed,
      currency,
      as_of_date: asOfDate,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="text-sm font-medium mb-1 block">Label</label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Edinburgh flat, Revolut business loan"
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>
      <div>
        <label className="text-sm font-medium mb-1 block">Category</label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="real_estate">Real Estate</SelectItem>
            <SelectItem value="other_asset">Other Asset</SelectItem>
            <SelectItem value="liability">Liability</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium mb-1 block">Amount</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">Currency</label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className="text-sm font-medium mb-1 block">As of date</label>
        <input
          type="date"
          value={asOfDate}
          onChange={(e) => setAsOfDate(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving}>{saving ? 'Saving...' : (initial ? 'Update' : 'Add')}</Button>
      </DialogFooter>
    </form>
  );
}

export function AssetRegisterDialog({ open, onOpenChange, entityId, defaultCurrency, onUpdate }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('list'); // list | add | edit
  const [editEntry, setEditEntry] = useState(null);

  const fetchEntries = async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const res = await assetRegisterAPI.list(entityId);
      setEntries(res.data || []);
    } catch (err) {
      toast.error('Failed to load register entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && entityId) {
      fetchEntries();
      setMode('list');
      setEditEntry(null);
    }
  }, [open, entityId]);

  const handleCreate = async (data) => {
    setSaving(true);
    try {
      await assetRegisterAPI.create(data);
      toast.success('Entry added');
      await fetchEntries();
      setMode('list');
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error('Failed to add entry');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (data) => {
    if (!editEntry) return;
    setSaving(true);
    try {
      await assetRegisterAPI.update(editEntry.id, data);
      toast.success('Entry updated');
      await fetchEntries();
      setMode('list');
      setEditEntry(null);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error('Failed to update entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await assetRegisterAPI.delete(id);
      toast.success('Entry deleted');
      await fetchEntries();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error('Failed to delete entry');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Assets & Liabilities Register</DialogTitle>
          <DialogDescription>
            Manage asset and liability entries for this entity. Liabilities are subtracted from assets to compute Net Assets.
          </DialogDescription>
        </DialogHeader>

        {mode === 'list' && (
          <div className="space-y-3">
            {loading ? (
              <p className="text-sm text-muted-foreground text-center py-4">Loading...</p>
            ) : entries.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No entries yet.</p>
            ) : (
              <div className="max-h-64 overflow-y-auto space-y-2">
                {entries.map((entry) => {
                  const meta = CATEGORY_META[entry.category] || CATEGORY_META.other_asset;
                  const Icon = meta.icon;
                  return (
                    <div key={entry.id} className="flex items-center justify-between p-2 rounded-md border text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate font-medium">{entry.label}</span>
                        <Badge variant="secondary" className={meta.color + ' text-[10px] px-1.5 py-0'}>
                          {meta.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <span className="font-mono text-xs">
                          {formatCurrency(entry.amount, entry.currency)}
                        </span>
                        <button
                          onClick={() => { setEditEntry(entry); setMode('edit'); }}
                          className="p-1 rounded hover:bg-muted"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="p-1 rounded hover:bg-muted text-red-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <Button variant="outline" size="sm" onClick={() => setMode('add')} className="w-full">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Entry
            </Button>
          </div>
        )}

        {mode === 'add' && (
          <EntryForm
            entityId={entityId}
            defaultCurrency={defaultCurrency}
            onSave={handleCreate}
            onCancel={() => setMode('list')}
            saving={saving}
          />
        )}

        {mode === 'edit' && editEntry && (
          <EntryForm
            initial={editEntry}
            entityId={entityId}
            defaultCurrency={defaultCurrency}
            onSave={handleUpdate}
            onCancel={() => { setMode('list'); setEditEntry(null); }}
            saving={saving}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
